import { getDocumentsWithToken } from "@services/azureDataApi";
import { SasTokenPrefix } from "@services/user/types";
import { useMutation } from "@tanstack/react-query";

const getExt = (u: string) => (u.split("?")[0].split("#")[0].split(".").pop() || "").toLowerCase();

const isPdf = (ext: string, mime: string) => ext === "pdf" || mime === "application/pdf";

const isImage = (ext: string, mime: string) =>
  mime.startsWith("image/") || ext === "jpg" || ext === "jpeg" || ext === "png";

const isOffice = (ext: string, mime: string) =>
  ext === "doc" ||
  ext === "docx" ||
  ext === "xls" ||
  ext === "xlsx" ||
  mime === "application/msword" ||
  mime === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
  mime === "application/vnd.ms-excel" ||
  mime === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const toViewableMime = (ext: string, mime: string) => {
  if (isPdf(ext, mime)) return "application/pdf";
  if (mime.startsWith("image/")) return mime;
  if (ext === "jpg" || ext === "jpeg") return "image/jpeg";
  if (ext === "png") return "image/png";
  return "application/octet-stream";
};

const toAbsoluteUrl = (u: string) =>
  /^https?:\/\//i.test(u) ? u : new URL(u, window.location.origin).toString();

export type DownloadType = "certificate" | "document";

export const useDownloadCertificate = () => {
  const certificateMutation = useMutation({
    mutationFn: async ({ url }: { service: string; url: string }) => {
      return getDocumentsWithToken(url, SasTokenPrefix.VESSEL_REGISTRATION);
    },
  });
  const documentMutation = useMutation({
    mutationFn: async ({ url }: { service: string; url: string }) => {
      return getDocumentsWithToken(url, SasTokenPrefix.VESSEL_REGISTRATION);
    },
  });

  const handleDownload = async (
    type: DownloadType,
    service: string,
    documentUrl: string,
    filename?: string,
  ) => {
    try {
      const mutateAsync =
        type === "certificate" ? certificateMutation.mutateAsync : documentMutation.mutateAsync;

      const defaultName = type === "certificate" ? "certificate.pdf" : "document.pdf";
      const blob = await mutateAsync({ service, url: documentUrl });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || defaultName;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  const handlePreview = async (type: DownloadType, service: string, documentUrl: string) => {
    try {
      const mutateAsync =
        type === "certificate" ? certificateMutation.mutateAsync : documentMutation.mutateAsync;
      const blob = await mutateAsync({ service, url: documentUrl });
      const ext = getExt(documentUrl);
      const mime = blob.type || "";

      if (isPdf(ext, mime) || isImage(ext, mime)) {
        const viewMime = toViewableMime(ext, mime);
        const objUrl = URL.createObjectURL(new Blob([blob], { type: viewMime }));
        window.open(objUrl, "_blank", "noopener,noreferrer");
        return;
      }

      // doesn't work, probably because files are not public
      if (isOffice(ext, mime)) {
        const src = encodeURIComponent(toAbsoluteUrl(documentUrl));
        const viewer = `https://view.officeapps.live.com/op/view.aspx?src=${src}`;
        window.open(viewer, "_blank", "noopener,noreferrer");
        return;
      }

      const objUrl = URL.createObjectURL(blob);
      window.open(objUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("Preview failed:", err);
    }
  };

  return {
    handleDownload,
    handlePreview,
    isPending: certificateMutation.isPending || documentMutation.isPending,
    isError: certificateMutation.isError || documentMutation.isError,
    error: certificateMutation.error || documentMutation.error,
  };
};
